import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { AlertTriangle } from "lucide-react";
import BaseModal from "../base/BaseModal";

export default function RejectDocumentModal({
  isOpen,
  onClose,
  onConfirm,
  document,
  loading = false,
}) {
  const [lyDoTuChoi, setLyDoTuChoi] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setLyDoTuChoi("");
      setError("");
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!lyDoTuChoi.trim()) {
      setError("Vui lòng nhập lý do từ chối");
      return;
    }
    if (onConfirm) {
      onConfirm(lyDoTuChoi.trim());
    }
  };

  if (!isOpen) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={loading ? undefined : onClose}
      title=""
      size="md"
      showCloseButton={false}
      className="p-0 overflow-hidden"
    >
      <div className="p-4 sm:p-5">
        {/* Header */}
        <div className="flex items-center gap-2.5 pb-3 border-b border-gray-100">
          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 text-red-600">
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 leading-tight">
              Từ chối tài liệu
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Vui lòng xác nhận lý do từ chối phê duyệt
            </p>
          </div>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          {/* Document Summary Info */}
          {document && (
            <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
              {document.so_hieu && (
                <div className="text-xs font-semibold text-blue-700 mb-0.5">
                  Số hiệu: {document.so_hieu}
                </div>
              )}
              <div className="text-xs font-medium text-gray-900 line-clamp-2" title={document.tieu_de}>
                {document.tieu_de || "---"}
              </div>
            </div>
          )}

          {/* Info Notice */}
          <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 leading-relaxed">
            Tài liệu này sẽ chuyển về trạng thái <strong>Nháp</strong> để người soạn thảo chỉnh sửa và gửi duyệt lại.
          </div>

          {/* Rejection Reason Input */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-700">
                Lý do từ chối <span className="text-red-500 font-medium">*</span>
              </label>
              <span className={`text-[11px] ${lyDoTuChoi.length > 450 ? "text-amber-600 font-medium" : "text-gray-400"}`}>
                {lyDoTuChoi.length}/500
              </span>
            </div>
            <textarea
              rows={3}
              maxLength={500}
              value={lyDoTuChoi}
              onChange={(e) => {
                setLyDoTuChoi(e.target.value);
                if (error && e.target.value.trim()) {
                  setError("");
                }
              }}
              disabled={loading}
              placeholder="Nhập lý do từ chối (bắt buộc)..."
              className={`w-full px-3 py-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors placeholder:text-gray-400 resize-none disabled:bg-gray-50 disabled:text-gray-500 ${
                error ? "border-red-500 bg-red-50/20" : "border-gray-300"
              }`}
            />
            {error && (
              <p className="mt-1 text-xs text-red-600 font-medium">{error}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2.5 pt-2.5 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-3.5 py-1.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading || !lyDoTuChoi.trim()}
              className="px-3.5 py-1.5 text-xs sm:text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {loading && (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              <span>{loading ? "Đang xử lý..." : "Xác nhận từ chối"}</span>
            </button>
          </div>
        </form>
      </div>
    </BaseModal>
  );
}

RejectDocumentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  document: PropTypes.object,
  loading: PropTypes.bool,
};
